import { useContext, useMemo } from 'react';
import { useStore } from 'zustand';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { ChannelManager } from 'app/typings/channelManagers';
import { AVAILABILITY_MODE } from 'app/typings/core/admin/experiences/enum/availabilityMode';
import { ExtranetExperienceStatus } from 'app/typings/core/extranet/hotel/experiences';

import {
  InventoryTabItemName,
  inventoryStore,
} from '../../../../InventoryStore';
import { useInventoryPermissions } from '../../../../hooks/useInventoryPermissions';
import { PackType } from '../../ActionsBar/InventoryToggle/InventoryToggle';
import {
  createExperiencesTabConfig,
  createPacksTabConfig,
  createRoomsTabConfig,
} from '../../ActionsBar/actionButtonPresets';

type ConfigHandlers = {
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onPricingConfiguration: () => void;
  onAutoInventory: () => void;
};

type ConfigOptions = {
  saveDisabled: boolean;
  saving: boolean;
  bulkEditIcon?: React.ReactNode;
  pricingConfigurationIcon?: React.ReactNode;
  autoInventoryIcon?: React.ReactNode;
};

type ToggleConfig = {
  value: PackType;
  onChange?: (value: PackType) => void;
};

export const useInventoryConfig = (
  handlers: ConfigHandlers,
  options: ConfigOptions = { saveDisabled: false, saving: false }
) => {
  const packType = useStore(inventoryStore, (state) => state.packType);
  const setPackType = useStore(inventoryStore, (state) => state.setPackType);
  const experienceInventoryType = useStore(
    inventoryStore,
    (state) => state.experienceInventoryType
  );
  const setExperienceInventoryType = useStore(
    inventoryStore,
    (state) => state.setExperienceInventoryType
  );
  const { experiencesList, publishedRooms, publishedPacks } =
    useContext(ModernHotelContext);
  const { hotelInfo } = useContext(HotelContext);
  const { isConnectedHotel: hasChannelManager, hasDefaultPricingRule } =
    useInventoryPermissions();

  const channelManager = hotelInfo?.channelManager;

  const { onEdit, onCancel, onSave, onPricingConfiguration, onAutoInventory } =
    handlers;
  const { saveDisabled, saving, pricingConfigurationIcon, autoInventoryIcon } =
    options;

  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);
  const tab = useStore(inventoryStore, (state) => state.tab);

  const hasPublishedExperiences = useMemo(() => {
    return experiencesList.some(
      (exp) => exp.status === ExtranetExperienceStatus.PUBLISHED
    );
  }, [experiencesList]);

  const hasTimeslotExperiences = useMemo(() => {
    return experiencesList.some(
      (exp) => exp.availabilityMode === AVAILABILITY_MODE.TIMESLOT
    );
  }, [experiencesList]);

  const hasNoPublishedRooms = publishedRooms.length === 0;

  const hasNoPublishedPacksOfType = useMemo(() => {
    const isDaytime = packType === PackType.DAYTIME;

    return !publishedPacks.some((pack) => pack.isDaytime === isDaytime);
  }, [publishedPacks, packType]);

  const configFactories = useMemo(
    () => ({
      [InventoryTabItemName.Packs]: () => {
        const toggleConfig: ToggleConfig = {
          value: packType,
          onChange: setPackType,
        };

        return createPacksTabConfig(
          isEditMode,
          toggleConfig,
          {
            onEdit,
            onCancel,
            onSave,
            onPricingConfiguration,
          },
          {
            saveDisabled,
            saving,
            pricingConfigurationIcon,
            hasNoPublishedPacks: hasNoPublishedPacksOfType,
            pricingConfigurationNudge: !hasDefaultPricingRule,
          }
        );
      },
      [InventoryTabItemName.Rooms]: () =>
        createRoomsTabConfig(
          isEditMode,
          {
            onEdit,
            onCancel,
            onSave,
            onPricingConfiguration,
            onAutoInventory:
              hotelInfo?.channelManager !== ChannelManager.NONE
                ? undefined
                : onAutoInventory,
          },
          hasChannelManager,
          channelManager,
          {
            saveDisabled,
            saving,
            pricingConfigurationIcon,
            pricingConfigurationNudge: !hasDefaultPricingRule,
            autoInventoryIcon,
            hasNoPublishedRooms,
          }
        ),
      [InventoryTabItemName.Experiences]: () =>
        createExperiencesTabConfig(
          isEditMode,
          {
            value: experienceInventoryType,
            onChange: setExperienceInventoryType,
          },
          {
            onEdit: !hasPublishedExperiences
              ? () => {
                  /* no-op */
                }
              : onEdit,
            onCancel,
            onSave,
          },
          {
            saveDisabled,
            saving,
          },
          !hasPublishedExperiences,
          hasTimeslotExperiences
        ),
    }),
    [
      packType,
      setPackType,
      experienceInventoryType,
      setExperienceInventoryType,
      isEditMode,
      onEdit,
      onCancel,
      onSave,
      onPricingConfiguration,
      saveDisabled,
      saving,
      pricingConfigurationIcon,
      hotelInfo?.channelManager,
      onAutoInventory,
      hasChannelManager,
      channelManager,
      autoInventoryIcon,
      hasPublishedExperiences,
      hasTimeslotExperiences,
      hasNoPublishedRooms,
      hasNoPublishedPacksOfType,
      hasDefaultPricingRule,
    ]
  );

  const config = useMemo(() => {
    const factory = configFactories[tab.name as InventoryTabItemName];

    return factory ? factory() : null;
  }, [tab.name, configFactories]);

  return config;
};
